import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, Button, Typography, Container, Box, Snackbar, Alert, DialogTitle, Dialog, DialogContent, DialogActions, DialogContentText } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { warn } from 'console';

interface ErrorDialogProps {
    error: string | null;
    onClose: () => void;
}

interface VerifyEmailDialogProps {
    open: boolean;
    onClose: () => void;
}

const VerifyEmailDialog: React.FC<VerifyEmailDialogProps> = ({ open, onClose }) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="verify-email-dialog-title"
            sx={{ '& .MuiDialog-paper': { borderRadius: '13px' } }}
        >
            <DialogTitle id="verify-email-dialog-title" sx={{ fontSize: '17px', fontWeight: 'Bold', textAlign: 'center' }}>Registration Successful</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ textAlign: 'justify' }}>
                    To complete your registration, please check your email inbox for a verification link.
                    If you do not receive the email within a few minutes, please check your{" "}
                    <strong>Spam</strong> or <strong>Junk</strong> folder.
                    We appreciate your prompt attention to this matter and look forward to your successful verification.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained" color="success" sx={{ width: '100%', textTransform: 'none', borderRadius: '8px' }}>
                    Got it
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const ErrorDialog: React.FC<ErrorDialogProps> = ({ error, onClose }) => {
    return (
        <Dialog open={Boolean(error)} onClose={onClose} sx={{ '& .MuiDialog-paper': { borderRadius: '13px' } }}>
            <DialogTitle sx={{ color: 'error.main', textAlign: 'center', fontWeight: 'bold' }}>{error}</DialogTitle>
            <DialogContent>
                <Typography sx={{ textAlign: 'justify', fontStyle: 'italic' }}>
                    {error === 'User already exists'
                        ? 'This email is already registered. Please try resetting your password via email, or use a different email address to register.'
                        : error === 'Registration failed'
                            ? 'Registration failed. Please contact the administrator at duitleh@tanshizhe.com for assistance.'
                            : error}
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{ width: '100%', textTransform: 'none', borderRadius: '8px' }} variant="contained" color='error'>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const Register: React.FC = () => {
    document.title = "Duitleh - Register";
    const navigate = useNavigate();
    const { register } = useAuth();
    const [loading, setLoading] = useState(false);
    const [successOpen, setSuccessOpen] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        username: '',
        password: '',
    });
    const [error, setError] = useState<string | null>('');
    const [errors, setErrors] = useState({
        name: '',
        email: '',
        phone: '',
        username: '',
        password: '',
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const newErrors: typeof errors = {
            name: '',
            email: '',
            phone: '',
            username: '',
            password: '',
        };

        if (formData.name.length < 6) newErrors.name = 'Full Name is required';
        if (!/^\S+@\S+\.\S+$/.test(formData.email)) newErrors.email = 'Invalid email';
        if (formData.phone && !/^01\d{8,9}$/.test(formData.phone)) newErrors.phone = 'Invalid phone';
        if (!/^[a-zA-Z0-9]{3,15}$/.test(formData.username)) newErrors.username = 'Username must be 3-15 alphanumeric';
        if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 chars';

        setErrors(newErrors);

        if (Object.values(newErrors).some(Boolean)) return;

        try {
            setLoading(true);
            await register(formData);
            setSuccessOpen(true);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Registration failed');
        } finally {
            setLoading(false);
        }

    };

    const handleSnackbarClose = () => {
        setSuccessOpen(false);
        navigate('/dashboard');
    };

    return (
        <Container maxWidth="xs">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <img src="/duitleh.png" alt="Duitleh Logo" style={{ width: 130, height: 'auto', marginBottom: 16 }} />
                <Typography component="h1" variant="h5" fontWeight={'Bold'}>
                    Register
                </Typography>
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="name"
                        label="Full Name"
                        name="name"
                        autoComplete="name"
                        autoFocus
                        value={formData.name}
                        onChange={handleChange}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '13px',
                            }
                        }}
                        error={!!errors.name}
                        helperText={errors.name}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Email Address"
                        name="email"
                        autoComplete="email"
                        value={formData.email}
                        onChange={handleChange}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '13px',
                            }
                        }}
                        error={!!errors.email}
                        helperText={errors.email}
                    />
                    <TextField
                        margin="normal"
                        fullWidth
                        id="phone"
                        label="Phone Number"
                        name="phone"
                        autoComplete="tel"
                        type='number'
                        value={formData.phone}
                        onChange={handleChange}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '13px',
                            }
                        }}
                        error={!!errors.phone}
                        helperText={errors.phone}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="username"
                        label="Username"
                        name="username"
                        autoComplete="username"
                        value={formData.username}
                        onChange={handleChange}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '13px',
                            }
                        }}
                        error={!!errors.username}
                        helperText={errors.username}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Password"
                        type="password"
                        id="password"
                        autoComplete="new-password"
                        value={formData.password}
                        onChange={handleChange}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '13px',
                            }
                        }}
                        error={!!errors.password}
                        helperText={errors.password}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{
                            mt: 3,
                            mb: 2,
                            borderRadius: '13px',
                            py: 1.5,
                            textTransform: 'none',
                        }}
                        loading={loading}
                    >
                        Sign Up
                    </Button>
                    {/* {error && (
                        <Typography color="error" sx={{ mt: 1, mb: 2, textAlign: 'center' }}>
                            {error}
                        </Typography>
                    )} */}
                    <ErrorDialog error={error} onClose={() => setError(null)} />
                    <Button
                        fullWidth
                        variant="text"
                        sx={{ textTransform: 'none' }}
                        onClick={() => navigate('/login')}
                    >
                        Already have an account? Sign In
                    </Button>

                </Box>
            </Box>

            <VerifyEmailDialog
                open={successOpen}
                onClose={() => {
                    setSuccessOpen(false);
                    navigate("/login"); // 跳转到登录页
                }} />
                
        </Container>


    );
};
